import { GatewayRegistrationCode } from "./GatewayRegistrationCode";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { storageKindLabel } from "@/lib/storage-kind-label";
import localFolderStructure from "@/assets/local-gateway/local-folder-structure.svg";
import sharedFolderStructure from "@/assets/local-gateway/shared-folder-structure.svg";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  CheckCircle2,
  Cloud,
  Database,
  Download,
  FolderOpen,
  Loader2,
  RefreshCw,
  Search,
  type LucideIcon,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useConnectionKinds,
  useCreateStorage,
  useIssuedLocalGatewayHandoffPolling,
  useIssueLocalGatewayRouteHandoff,
  useIssueLocalGatewaySetupHandoff,
} from "./hooks/useStorages";
import { normalizeSmbBasePathInput } from "./smb-root";
import {
  clearStorageBrowseWizardState,
  readStorageBrowseWizardState,
  redirectToStorageBrowse,
  startStorageBrowseSession,
  writeStorageBrowseWizardState,
} from "./storage-registration";
import type { LocalGatewayHandoffResponse } from "@/types/api";
import { useLocalGatewayHandoffMemory } from "./LocalGatewayHandoffContext";
import { copyTextToClipboard } from "@/lib/clipboard";
import {
  STORAGE_CONNECTION_KIND_CATEGORY_LABELS,
  STORAGE_CONNECTION_KIND_CATEGORY_ORDER,
  type StorageConnectionKindCategory,
  type StorageConnectionKindId,
  type StorageConnectionKindSummary,
  type StorageRegistrationProcedure,
} from "@/types/storage-registration";
import { StorageConnectionKindIcon } from "./StorageConnectionKindIcon";
import { LocalGatewayInstallerDownload, detectInstallerOs } from "./LocalGatewayInstallerDownload";
import { getActiveOrganizationScopeId } from "@/lib/organization-scope";
import { LocalGatewayConsolePreparation } from "./LocalGatewayConsolePreparation";

const CATEGORY_ICONS: Record<StorageConnectionKindCategory, LucideIcon> = {
  cloud_file_storage: Cloud,
  local_file_server: FolderOpen,
  object_storage: Database,
};

type LocalGatewayInstallStatus = "unknown" | "installed" | "not_installed";


function groupKindsByCategory(
  connectionKinds: StorageConnectionKindSummary[],
) {
  const groups = new Map<StorageConnectionKindCategory, StorageConnectionKindSummary[]>();
  for (const category of STORAGE_CONNECTION_KIND_CATEGORY_ORDER) {
    groups.set(category, []);
  }

  for (const kind of connectionKinds) {
    const group = groups.get(kind.connectionKindCategory);
    if (!group) {
      return {
        groupedKinds: [] as {
          category: StorageConnectionKindCategory;
          kinds: StorageConnectionKindSummary[];
        }[],
        unsupportedCategory: kind.connectionKindCategory as string,
      };
    }
    group.push(kind);
  }

  groups.get("local_file_server")?.sort((a, b) =>
    (a.connectionKindId === "local_folder" ? 0 : 1) - (b.connectionKindId === "local_folder" ? 0 : 1));
  return {
    groupedKinds: STORAGE_CONNECTION_KIND_CATEGORY_ORDER.map((category) => ({
      category,
      kinds: groups.get(category) ?? [],
    })).filter(({ kinds }) => kinds.length > 0),
    unsupportedCategory: null,
  };
}

function supportsCloudBrowse(kind: StorageConnectionKindSummary) {
  return kind.allowedProcedures.some(
    (procedure) => procedure.registrationProcedure === "browse_select_cloud",
  );
}

interface ProcedureGuideCopy {
  title: string;
  description: string;
  cta: string;
}

export function StorageNewPage() {
  const organizationScopeId = getActiveOrganizationScopeId();
  const { t } = useTranslation("storages");
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { connectionKindId: routeConnectionKindId = "" } = useParams<{
    connectionKindId?: string;
  }>();
  const { connectionKinds, isLoading, error, refresh } = useConnectionKinds();
  const { mutate: createStorage, isLoading: isSubmitting, error: submitError } =
    useCreateStorage();
  const {
    mutate: issueLocalGatewaySetupHandoff,
    isLoading: isIssuingSetupHandoff,
    error: setupHandoffError,
  } = useIssueLocalGatewaySetupHandoff();
  const {
    mutate: issueLocalGatewayRouteHandoff,
    isLoading: isIssuingRouteHandoff,
    error: routeHandoffError,
  } = useIssueLocalGatewayRouteHandoff();
  const shouldResumeWizard = Boolean(
    (
      location.state as
        | {
            resumeStorageWizard?: boolean;
          }
        | null
    )?.resumeStorageWizard,
  );

  const [selectedKindId, setSelectedKindId] = useState<StorageConnectionKindId | "">(
    "",
  );
  const [selectedProcedure, setSelectedProcedure] = useState<
    StorageRegistrationProcedure | ""
  >("");
  const [resourceLocatorInput, setResourceLocatorInput] = useState("");
  const [microsoftConnectionMode, setMicrosoftConnectionMode] = useState<"standard" | "site_search">("standard");
  const isMicrosoft = selectedKindId === "microsoft_onedrive" || selectedKindId === "sharepoint";
  const [smbHost, setSmbHost] = useState("");
  const [smbShareName, setSmbShareName] = useState("");
  const [smbBasePath, setSmbBasePath] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [displayNameTouched, setDisplayNameTouched] = useState(false);
  const [isStartingBrowse, setIsStartingBrowse] = useState(false);
  const [browseError, setBrowseError] = useState<string | null>(null);
  const handoffMemory = useLocalGatewayHandoffMemory();
  const [setupHandoff, setLocalSetupHandoff] =
    useState<LocalGatewayHandoffResponse | null>(null);
  const setSetupHandoff = useCallback((handoff: LocalGatewayHandoffResponse | null) => {
    if (handoff) handoffMemory.remember(handoff);
    setLocalSetupHandoff(handoff);
  }, [handoffMemory]);
  const [copiedSetupField, setCopiedSetupField] = useState<
    "registrationToken" | null
  >(null);
  const [smbCreatedStorageId, setSmbCreatedStorageId] = useState<string | null>(null);
  const [localGatewayInstallStatus, setLocalGatewayInstallStatus] =
    useState<LocalGatewayInstallStatus>("unknown");
  const [gatewayPrepared, setGatewayPrepared] = useState(false);
  const [installerOs, setInstallerOs] = useState(detectInstallerOs);
  const canIssueInitialHandoff = gatewayPrepared && localGatewayInstallStatus === "installed" &&
    (installerOs === "windows" || installerOs === "darwin");
  const handoffRequest = useRef<object | null>(null);
  useEffect(() => {
    return () => { handoffRequest.current = null; };
  }, [organizationScopeId, selectedKindId, installerOs]);
  const displayNameInputRef = useRef<HTMLInputElement | null>(null);
  const handleRegisteredHandoff = useCallback(
    (storageId: string) => {
      handoffMemory.forget(storageId);
      clearStorageBrowseWizardState(organizationScopeId);
      navigate(`/console/storages/${storageId}`);
    },
    [navigate, organizationScopeId, handoffMemory],
  );
  const {
    pollingStatus: setupHandoffPollingStatus,
    isExpired: setupHandoffExpired,
    isChecking: isCheckingSetupHandoff,
    error: setupHandoffPollingError,
    refreshRegistrationStatus,
  } = useIssuedLocalGatewayHandoffPolling(
    setupHandoff,
    handleRegisteredHandoff,
    { onDiscardHandoff: handoffMemory.forget },
  );

  const selectedKind = useMemo(
    () =>
      connectionKinds.find((kind) => kind.connectionKindId === selectedKindId) ?? null,
    [connectionKinds, selectedKindId],
  );
  const routeSelectedKind = useMemo(
    () =>
      routeConnectionKindId
        ? connectionKinds.find(
            (kind) => kind.connectionKindId === routeConnectionKindId,
          ) ?? null
        : null,
    [connectionKinds, routeConnectionKindId],
  );

  const { groupedKinds, unsupportedCategory } = useMemo(
    () => groupKindsByCategory(connectionKinds),
    [connectionKinds],
  );
  const showDisplayNameRequired =
    displayNameTouched && displayName.trim().length === 0;

  const persistSelection = useCallback(
    (patch: Partial<{ connectionKindId: StorageConnectionKindId | ""; selectedProcedure: StorageRegistrationProcedure | ""; browseSessionId: string; microsoftConnectionMode: "standard" | "site_search" }>) => {
      const current = readStorageBrowseWizardState(organizationScopeId) ?? {};
      const next = {
        ...current,
        ...patch,
      };
      if (!next.connectionKindId && !next.selectedProcedure && !next.browseSessionId) {
        clearStorageBrowseWizardState(organizationScopeId);
        return;
      }
      writeStorageBrowseWizardState(next, organizationScopeId);
    },
    [organizationScopeId],
  );

  useEffect(() => {
    if (shouldResumeWizard) {
      const storedWizardState = readStorageBrowseWizardState(organizationScopeId);
      setSelectedKindId(storedWizardState?.connectionKindId ?? "");
      setSelectedProcedure(storedWizardState?.selectedProcedure ?? "");
      return;
    }

    if (routeSelectedKind) {
      const firstProcedure =
        routeSelectedKind.allowedProcedures[0]?.registrationProcedure ?? "";
      const selectedRouteProcedure =
        routeSelectedKind.allowedProcedures.length === 1 ? firstProcedure : "";
      clearStorageBrowseWizardState(organizationScopeId);
      setSelectedKindId(routeSelectedKind.connectionKindId);
      setSelectedProcedure(selectedRouteProcedure);
      persistSelection({
        connectionKindId: routeSelectedKind.connectionKindId,
        selectedProcedure: selectedRouteProcedure,
      });
      return;
    }

    clearStorageBrowseWizardState(organizationScopeId);
    setSelectedKindId("");
    setSelectedProcedure("");
  }, [organizationScopeId, persistSelection, routeSelectedKind, shouldResumeWizard]);

  useEffect(() => {
    if (!selectedKindId) {
      return;
    }

    persistSelection({
      connectionKindId: selectedKindId,
      selectedProcedure: selectedProcedure || "",
    });
  }, [persistSelection, selectedKindId, selectedProcedure]);

  useEffect(() => {
    if (!selectedKind) return;
    displayNameInputRef.current?.focus();
  }, [selectedKind?.connectionKindId, gatewayPrepared]);

  useEffect(() => {
    if (!selectedKind) return;
    if (!selectedProcedure) return;
    if (
      !selectedKind.allowedProcedures.some(
        (procedure) => procedure.registrationProcedure === selectedProcedure,
      )
    ) {
      setSelectedProcedure("");
    }
  }, [selectedKind, selectedProcedure]);

  const resetStepTwo = useCallback(() => {
    clearStorageBrowseWizardState(organizationScopeId);
    setSelectedKindId("");
    setSelectedProcedure("");
    setResourceLocatorInput("");
    setSmbHost("");
    setSmbShareName("");
    setSmbBasePath("");
    setDisplayName("");
    setDisplayNameTouched(false);
    setBrowseError(null);
    setSetupHandoff(null);
    setCopiedSetupField(null);
    setSmbCreatedStorageId(null);
    setGatewayPrepared(false);
    setLocalGatewayInstallStatus("unknown");
  }, [organizationScopeId, setSetupHandoff]);

  const chooseKind = useCallback(
    (kind: StorageConnectionKindSummary) => {
      setBrowseError(null);
      setResourceLocatorInput("");
      setSmbHost("");
      setSmbShareName("");
      setSmbBasePath("");
      setDisplayName("");
      setDisplayNameTouched(false);
      setSetupHandoff(null);
      setCopiedSetupField(null);
      setSmbCreatedStorageId(null);
      setGatewayPrepared(false);
      setLocalGatewayInstallStatus("unknown");
      setSelectedKindId(kind.connectionKindId);
      setMicrosoftConnectionMode(kind.microsoftConnectionModes?.includes("standard") ? "standard" : kind.microsoftConnectionModes?.[0] ?? "standard");
      navigate(`/console/storages/new/${kind.connectionKindId}`);

      const firstProcedure = kind.allowedProcedures[0]?.registrationProcedure ?? "";
      if (kind.allowedProcedures.length === 1) {
        setSelectedProcedure(firstProcedure);
      } else {
        setSelectedProcedure("");
      }
      persistSelection({
        connectionKindId: kind.connectionKindId,
        selectedProcedure: kind.allowedProcedures.length === 1 ? firstProcedure : "",
      });
    },
    [navigate, persistSelection, setSetupHandoff],
  );

  const handleDirectCreate = useCallback(async () => {
    if (!selectedKind || !selectedProcedure) return;
    if (selectedKind.connectionKindId === "smb" && !canIssueInitialHandoff) return;
    if (handoffRequest.current) return;
    const request = {};
    handoffRequest.current = request;

    setBrowseError(null);
    try {
      const result = await createStorage({
        connectionKindId: selectedKind.connectionKindId,
        registrationProcedure: selectedProcedure,
        resourceLocatorInput: resourceLocatorInput.trim() || undefined,
        smbHost: smbHost.trim() || undefined,
        smbShareName: smbShareName.trim() || undefined,
        smbBasePath: normalizeSmbBasePathInput(smbBasePath) ?? undefined,
        displayName: displayName.trim(),
      });
      if (handoffRequest.current !== request) return;
      clearStorageBrowseWizardState(organizationScopeId);

      // SMB: issue handoff inline instead of navigating.
      // Handoff failures surface via routeHandoffError (shown in inlineActionError);
      // the user stays on this page so they can retry, rather than silently skipping
      // the handoff and landing on the detail page without any setup instructions.
      if (selectedKind.connectionKindId === "smb" && result.storageId) {
        setSmbCreatedStorageId(result.storageId);
        const handoff = await issueLocalGatewayRouteHandoff({
          storageId: result.storageId,
        });
        if (handoffRequest.current !== request) return;
        setSetupHandoff(handoff);
        setCopiedSetupField(null);
        return;
      }

      navigate(`/console/storages/${result.storageId}`);
    } catch (err) {
      // Error UI is driven by submitError / setupHandoffError / routeHandoffError / browseError
      // via `inlineActionError`; log for observability in case that contract breaks.
      console.error("[storage-new] submit failed", err);
    } finally {
      if (handoffRequest.current === request) handoffRequest.current = null;
    }
  }, [
    canIssueInitialHandoff,
    createStorage,
    displayName,
    issueLocalGatewayRouteHandoff,
    navigate,
    organizationScopeId,
    resourceLocatorInput,
    smbBasePath,
    smbHost,
    smbShareName,
    selectedKind,
    selectedProcedure,
    setSetupHandoff,
  ]);

  const handleIssueSetupHandoff = useCallback(async () => {
    if (selectedKind?.connectionKindId !== "local_folder" || !canIssueInitialHandoff || handoffRequest.current) {
      return;
    }

    const request = {};
    handoffRequest.current = request;
    try {
      const result = await issueLocalGatewaySetupHandoff({
        connectionKindId: "local_folder",
        displayName: displayName.trim(),
      });
      if (handoffRequest.current !== request) return;
      setSetupHandoff(result);
      setCopiedSetupField(null);
    } catch {
      // The hook exposes the failure through setupHandoffError. Do not reissue automatically.
    } finally {
      if (handoffRequest.current === request) handoffRequest.current = null;
    }
  }, [canIssueInitialHandoff, issueLocalGatewaySetupHandoff, selectedKind, displayName, setSetupHandoff]);

  const handleCopyRegistrationToken = useCallback(async (
    event?: MouseEvent<HTMLButtonElement>,
  ) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!setupHandoff) {
      return;
    }
    await copyTextToClipboard(setupHandoff.registrationToken);
    setCopiedSetupField("registrationToken");
  }, [setupHandoff]);

  const handleStartBrowse = useCallback(async () => {
    if (!selectedKind || !supportsCloudBrowse(selectedKind)) return;
    const storageDisplayName = displayName.trim();
    if (!storageDisplayName) return;

    setIsStartingBrowse(true);
    setBrowseError(null);
    try {
      const session = await startStorageBrowseSession({
        ...(isMicrosoft ? { microsoftConnectionMode } : {}),
        connectionKindId: selectedKind.connectionKindId,
        storageDisplayName,
      });
      persistSelection({
        browseSessionId: session.browseSessionId,
        connectionKindId: selectedKind.connectionKindId,
        selectedProcedure: "browse_select_cloud",
        ...(isMicrosoft ? { microsoftConnectionMode } : {}),
      });
      redirectToStorageBrowse(session.redirectStartUrl);
    } catch (err) {
      setBrowseError(err instanceof Error ? err.message : t("new.errors.browseStartFailed"));
    } finally {
      setIsStartingBrowse(false);
    }
  }, [displayName, persistSelection, selectedKind, t, isMicrosoft, microsoftConnectionMode]);

  useEffect(() => {
    if (isLoading || error || !selectedKindId || selectedKind) return;
    clearStorageBrowseWizardState(organizationScopeId);
    setSelectedKindId("");
    setSelectedProcedure("");
  }, [isLoading, error, selectedKindId, selectedKind, organizationScopeId]);

  const selectedKindHasMultipleProcedures =
    (selectedKind?.allowedProcedures.length ?? 0) > 1;
  const invalidRouteConnectionKind =
    Boolean(routeConnectionKindId) && !isLoading && !routeSelectedKind;

  const pageError =
    error ??
    (unsupportedCategory
      ? t("new.errors.unsupportedCategory", { category: unsupportedCategory })
      : invalidRouteConnectionKind
        ? t("new.errors.unsupportedConnectionKind", {
            connectionKindId: routeConnectionKindId,
          })
      : null);

  const inlineActionError =
    browseError ?? submitError ?? routeHandoffError ?? setupHandoffError;
  const isIssuingAnyHandoff = isIssuingSetupHandoff || isIssuingRouteHandoff;
  const selectLocalGatewayInstallStatus = (status: LocalGatewayInstallStatus) => {
    setLocalGatewayInstallStatus(status);
  };

  const needsGatewayPreparation = selectedKind?.connectionKindId === "local_folder" || selectedKind?.connectionKindId === "smb";
  const isLocalGatewayInstalled = localGatewayInstallStatus === "installed";
  const localGatewayInstallTarget =
    selectedKind?.connectionKindId === "smb"
      ? t("new.localGatewayInstallTargetSmb")
      : t("new.localGatewayInstallTargetLocalFolder");
  const localGatewayInstallGate = selectedKind ? (
    <section className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Button
          type="button"
          variant={isLocalGatewayInstalled ? "default" : "outline"}
          aria-pressed={isLocalGatewayInstalled}
          disabled={isIssuingAnyHandoff || isSubmitting}
          onClick={() => selectLocalGatewayInstallStatus("installed")}
          className="justify-start"
        >
          <CheckCircle2 className="mr-2 h-4 w-4" />
          {t("new.localGatewayInstalledCta")}
        </Button>
        <Button
          type="button"
          variant={
            localGatewayInstallStatus === "not_installed" ? "default" : "outline"
          }
          aria-pressed={localGatewayInstallStatus === "not_installed"}
          disabled={isIssuingAnyHandoff || isSubmitting}
          onClick={() => selectLocalGatewayInstallStatus("not_installed")}
          className="justify-start"
        >
          <Download className="mr-2 h-4 w-4" />
          {t("new.localGatewayNotInstalledCta")}
        </Button>
      </div>
      {isLocalGatewayInstalled ? <LocalGatewayConsolePreparation
        os={installerOs} busy={isIssuingAnyHandoff || isSubmitting}
        onOsChange={value => { setInstallerOs(value); }}
      /> : null}
      {localGatewayInstallStatus === "not_installed" ? (
        <div className="space-y-5">
          <LocalGatewayInstallerDownload os={installerOs} onOsChange={setInstallerOs} />
          <div className="space-y-3">
            <p>{t("new.localGatewayInstallNextBody")}</p>
            <Button type="button" onClick={() => selectLocalGatewayInstallStatus("installed")}>
              {t("new.localGatewayInstallCompleteCta")}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  ) : null;

  return (
    <div className="space-y-8 p-6">
      <div className="rounded-[28px] border border-slate-200 bg-gradient-to-br from-white via-slate-50/80 to-slate-100/70 p-6 shadow-sm">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (selectedKind) {
                resetStepTwo();
                navigate("/console/storages/new");
                return;
              }
              navigate("/console/storages");
            }}
            data-storage-kind-back
            aria-label={selectedKind ? t("new.changeConnectionKind") : tc("actions.back")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {selectedKind && (
            <div className="rounded-lg bg-muted p-2 text-muted-foreground">
              <StorageConnectionKindIcon
                connectionKindId={selectedKind.connectionKindId}
              />
            </div>
          )}
          <div className="min-w-0 space-y-1">
            <h1 className="truncate text-2xl font-semibold tracking-tight text-slate-950">
              {selectedKind
                ? t("new.step2Title", {
                    kind: storageKindLabel(selectedKind.connectionKindId, selectedKind.connectionKindLabel),
                  })
                : t("new.step1Title")}
            </h1>
            {!selectedKind && (
              <p className="text-sm leading-6 text-slate-600">
                {t("new.step1Description")}
              </p>
            )}
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-4">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="h-32 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && pageError && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="space-y-3 p-6">
            <p className="text-sm text-destructive">{pageError}</p>
            <div className="flex gap-2">
              <Button onClick={() => void refresh()}>{tc("actions.reload")}</Button>
              <Button variant="outline" onClick={() => navigate(invalidRouteConnectionKind ? "/console/storages/new" : "/console/storages")}>
                {tc("actions.back")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLoading && !pageError && !selectedKind && (
        <div className="space-y-8">
          {groupedKinds.length === 0 && <p role="status">{t("new.noRegistrationAvailable")}</p>}
          {groupedKinds.map(({ category, kinds }) => {
            const CategoryIcon = CATEGORY_ICONS[category];
            return (
              <section key={category} className="space-y-4">
                <div className="flex items-center gap-3">
                  <CategoryIcon className="h-5 w-5 text-primary" />
                  <h2 className="text-lg font-semibold">
                    {STORAGE_CONNECTION_KIND_CATEGORY_LABELS[category]}
                  </h2>
                </div>

                {category === "local_file_server" ? <div className="space-y-1 text-sm text-slate-600"><p>{t("new.folderChoiceLead")}</p><p>{t("new.folderCompatibility")}</p></div> : null}
                <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,340px),340px))] gap-4">
                  {kinds.map((kind) => {
                    const folderKind = kind.connectionKindId === "local_folder" || kind.connectionKindId === "smb";
                    const local = kind.connectionKindId === "local_folder";
                    return (
                      <button
                        key={kind.connectionKindId}
                        type="button"
                        className="min-w-0 select-none rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:translate-y-px"
                        aria-label={storageKindLabel(kind.connectionKindId, kind.connectionKindLabel)}
                        onClick={() => chooseKind(kind)}
                      >
                        <Card
                          className={`h-full shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:border-primary hover:shadow-md ${
                            selectedKindId === kind.connectionKindId
                              ? "border-primary bg-primary/5 shadow"
                              : ""
                          }`}
                        >
                          <CardContent className={folderKind ? "flex h-full flex-col gap-3 p-5" : "flex items-center gap-3 p-4"}>
                            {folderKind ? <>
                              <div className="text-lg font-semibold text-slate-950">{storageKindLabel(kind.connectionKindId, kind.connectionKindLabel)}</div>
                              <p className="text-sm leading-6 text-slate-600">{t(local ? "new.localFolderChoice" : "new.sharedFolderChoice")}</p>
                              <img src={local ? localFolderStructure : sharedFolderStructure}
                                alt={t(local ? "new.localFolderStructureAlt" : "new.sharedFolderStructureAlt")}
                                className="mx-auto aspect-[12/5] w-full max-w-[440px] object-cover"
                                onError={event => { event.currentTarget.hidden = true; }} />
                              <span className="mt-auto text-sm font-medium text-primary">{t(local ? "new.localFolderChoose" : "new.sharedFolderChoose")} →</span>
                            </> : <>

                            <div className="rounded-lg bg-muted p-2 text-muted-foreground">
                              <StorageConnectionKindIcon
                                connectionKindId={kind.connectionKindId}
                              />
                            </div>
                            <div className="min-w-0 flex-1 text-base font-semibold text-slate-950">
                              {storageKindLabel(kind.connectionKindId, kind.connectionKindLabel)}
                            </div>
                            </>}
                          </CardContent>
                        </Card>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {!isLoading && !pageError && selectedKind && needsGatewayPreparation && !gatewayPrepared && (
        <section className="space-y-5 rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-semibold">{t("new.gatewayPreparationTitle")}</h2>
          <p>{t("new.localGatewayInstallGateBody", { target: localGatewayInstallTarget })}</p>
          {localGatewayInstallGate}
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => navigate("/console/storages/new")}>{t("new.reselectStorageFamily")}</Button>
            {isLocalGatewayInstalled && (installerOs === "windows" || installerOs === "darwin") ?
              <Button type="button" onClick={() => setGatewayPrepared(true)}>{t("new.gatewayPreparationNext")}</Button> : null}
          </div>
        </section>
      )}
      {!isLoading && !pageError && selectedKind && (!needsGatewayPreparation || gatewayPrepared) && (
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
          <div className="space-y-2 border-b border-slate-200 px-6 py-5">
            <Label htmlFor="displayName">{t("new.displayNameLabel")}</Label>
            <Input
              id="displayName"
              ref={displayNameInputRef}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onBlur={(event) => {
                if (event.relatedTarget instanceof HTMLElement && event.relatedTarget.hasAttribute("data-storage-kind-back")) return;
                setDisplayNameTouched(true);
              }}
              aria-invalid={showDisplayNameRequired}
              aria-describedby={
                showDisplayNameRequired ? "displayName-error" : undefined
              }
              placeholder={t("new.displayNamePlaceholder")}
            />
            {showDisplayNameRequired && (
              <p role="alert" id="displayName-error" className="text-sm font-medium text-destructive">
                {t("new.displayNameRequired")}
              </p>
            )}
            {needsGatewayPreparation ? <Button type="button" variant="outline" data-storage-kind-back onClick={() => setGatewayPrepared(false)}>{t("new.gatewayPreparationBack")}</Button> : (
            <Button type="button" variant="outline" data-storage-kind-back onClick={() => {
              resetStepTwo();
              navigate("/console/storages/new");
            }}>
              {t("new.reselectStorageFamily")}
            </Button>
            )}
          </div>

          <div className="px-6 py-6">
            <div className="space-y-6">
              {selectedKindHasMultipleProcedures && !selectedProcedure && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold tracking-tight text-slate-950">
                      {t("new.procedureChooserTitle")}
                    </h3>
                    <p className="text-sm leading-6 text-slate-600">
                      {t("new.procedureChooserDescription")}
                    </p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {selectedKind.allowedProcedures.map((procedure) => {
                      const procedureGuide = t(
                        `new.procedureGuides.${procedure.registrationProcedure}`,
                        {
                          returnObjects: true,
                        },
                      ) as unknown as ProcedureGuideCopy;
                      return (
                        <button
                          key={procedure.registrationProcedure}
                          type="button"
                          className="select-none rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:border-primary hover:bg-primary/5 hover:shadow-md active:translate-y-0 active:shadow-sm"
                          onClick={() => {
                            setSelectedProcedure(procedure.registrationProcedure);
                            persistSelection({
                              connectionKindId: selectedKind.connectionKindId,
                              selectedProcedure: procedure.registrationProcedure,
                            });
                          }}
                        >
                          <div className="space-y-2">
                            <div className="font-medium text-slate-950">
                              {procedureGuide.title}
                            </div>
                            <p className="text-sm leading-6 text-slate-600">
                              {procedureGuide.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedProcedure === "browse_select_cloud" && (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                    {isMicrosoft ? "接続方法を選んでからMicrosoftにサインインしてください。" : t("new.browseCloudHelp")}
                  </div>
                  {isMicrosoft && <fieldset className="space-y-3">
                    <legend className="font-medium">Microsoftストレージへの接続方法</legend>
                    <label className="block rounded-lg border p-4">
                      <input type="radio" name="microsoftConnectionMode" value="standard" disabled={!selectedKind?.microsoftConnectionModes?.includes("standard")} checked={microsoftConnectionMode === "standard"} onChange={() => setMicrosoftConnectionMode("standard")} />
                      <span className="ml-2 font-medium">通常の接続（おすすめ）</span>
                      <p className="mt-2 text-sm">自分のOneDrive、または共有リンクで指定したSharePointのフォルダを利用します。一般ユーザーはこちらを選んでください。</p>
                    </label>
                    <label className="block rounded-lg border p-4">
                      <input type="radio" name="microsoftConnectionMode" value="site_search" disabled={!selectedKind?.microsoftConnectionModes?.includes("site_search")} checked={microsoftConnectionMode === "site_search"} onChange={() => setMicrosoftConnectionMode("site_search")} />
                      <span className="ml-2 font-medium">サイトを検索して接続（管理者向け）</span>
                      <p className="mt-2 text-sm">SharePointのサイト・ライブラリを検索し、フォルダを選択します。サイト検索のための追加アクセス許可を求めます。職場・学校アカウント向けです。</p>
                    </label>
                    <p className="text-sm text-muted-foreground">どちらも、ご自身がアクセスできる保存先を利用します。組織の設定によっては、管理者の承認が必要です。</p>
                    <details className="text-sm"><summary>要求するアクセス許可</summary><p>アカウントの確認と、アクセスできるファイルの読み書き・継続利用を許可します。{microsoftConnectionMode === "site_search" ? "サイト検索のため、サイト情報の読み取りも許可します。" : "サイト検索の権限は要求しません。"}Microsoftへの許可範囲は、登録するフォルダだけに限定されるものではありません。</p></details>
                  </fieldset>}
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => void handleStartBrowse()}
                      disabled={isStartingBrowse || !displayName.trim() || (isMicrosoft && !selectedKind?.microsoftConnectionModes?.includes(microsoftConnectionMode))}
                    >
                      {isStartingBrowse ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t("new.browseStarting")}
                        </>
                      ) : (
                        <>
                          <Search className="mr-2 h-4 w-4" />
                          {isMicrosoft ? "Microsoftにサインイン" : t("new.browseCloudCta")}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {setupHandoff ? <section className="space-y-3 rounded-xl border p-5">
                <h3 className="font-semibold">{t("new.installerDownloadTitle")}</h3>
                <LocalGatewayInstallerDownload key={organizationScopeId} os={installerOs} onOsChange={setInstallerOs} />
              </section> : null}

              {selectedProcedure === "browse_select_local" &&
                selectedKind.connectionKindId === "local_folder" && (
                  <div className="space-y-5">
                    {!setupHandoff ? (
                      <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold tracking-tight text-slate-950">
                            {t("new.localFolderTitle")}
                          </h3>
                        </div>
                        <div className="flex flex-wrap gap-3">
                          {canIssueInitialHandoff ? (
                            <Button
                              onClick={() => void handleIssueSetupHandoff()}
                              disabled={isIssuingAnyHandoff || !displayName.trim()}
                            >
                              {isIssuingAnyHandoff
                                ? t("detail.localGatewaySetupIssuing")
                                : t("detail.localGatewaySetupIssueCta")}
                            </Button>
                          ) : null}
                        </div>
                      </section>
                    ) : (
                      <section className="space-y-4 rounded-2xl border border-sky-200 bg-sky-50/70 p-5">
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold tracking-tight text-slate-950">
                            {t("detail.localGatewaySetupIssuedTitle")}
                          </h3>
                          <p className="text-sm leading-6 text-slate-700">
                            {t("new.localFolderIssuedBody")}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-slate-900">
                            {t("new.localFolderNextStepsTitle")}
                          </p>
                          <ol className="list-decimal space-y-1 pl-5 text-sm leading-6 text-slate-600">
                            {(
                              t("new.localFolderNextSteps", {
                                returnObjects: true,
                              }) as unknown as string[]
                            ).map((step) => (
                              <li key={step}>{step}</li>
                            ))}
                          </ol>
                        </div>
                        <GatewayRegistrationCode
                          registrationToken={setupHandoff.registrationToken}
                          expiresAt={setupHandoff.expiresAt}
                          copied={copiedSetupField === "registrationToken"}
                          onCopy={(event) => void handleCopyRegistrationToken(event)}
                        />
                        <div className="flex flex-wrap gap-3">
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            aria-label={isCheckingSetupHandoff ? t("detail.rechecking") : t("new.localGatewaySetupCheckStatus")}
                            onClick={() => void refreshRegistrationStatus()}
                            disabled={isCheckingSetupHandoff || isIssuingAnyHandoff}
                          >
                            {isCheckingSetupHandoff ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}

                          </Button>
                              </TooltipTrigger>
                              <TooltipContent>{t("new.localGatewaySetupCheckStatus")}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {setupHandoffExpired ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void handleIssueSetupHandoff()}
                              disabled={isIssuingAnyHandoff}
                            >
                              {isIssuingAnyHandoff
                                ? t("detail.localGatewaySetupIssuing")
                                : t("detail.localGatewaySetupReissueCta")}
                            </Button>
                          ) : null}
                        </div>
                        {setupHandoffPollingStatus === "polling" ? (
                          <div className="flex items-center gap-2 rounded-xl border border-sky-200 bg-white/90 p-3 text-sm text-sky-800">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t("detail.pollingActive")}
                          </div>
                        ) : null}
                        {setupHandoffExpired ? (
                          <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3 text-sm text-amber-900">
                            {t("new.localFolderHandoffExpired")}
                          </div>
                        ) : null}
                        {setupHandoffPollingError ? (
                          <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3 text-sm text-amber-900">
                            {t("new.localFolderRegistrationCheckFailed")}
                          </div>
                        ) : null}

                      </section>
                    )}
                  </div>
                )}

              {selectedProcedure === "register_existing" &&
                selectedKind.connectionKindId === "smb" && (
                <div className="space-y-4">
                  {!smbCreatedStorageId ? (
                    <>
                      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] md:items-start">
                        <div className="space-y-2">
                          <Label htmlFor="smbHost">{t("new.smbHostLabel")}</Label>
                          <Input
                            id="smbHost"
                            value={smbHost}
                            onChange={(e) => setSmbHost(e.target.value)}
                            placeholder={t("new.smbHostPlaceholder")}
                          />
                        </div>
                        <div
                          aria-hidden="true"
                          className="hidden pt-8 text-xl font-semibold text-slate-400 md:block"
                        >
                          /
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="smbShareName">
                            {t("new.smbShareNameLabel")}
                          </Label>
                          <Input
                            id="smbShareName"
                            value={smbShareName}
                            onChange={(e) => setSmbShareName(e.target.value)}
                            placeholder={t("new.smbShareNamePlaceholder")}
                          />
                        </div>
                        <div
                          aria-hidden="true"
                          className="hidden pt-8 text-xl font-semibold text-slate-400 md:block"
                        >
                          /
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="smbBasePath">
                            {t("new.smbBasePathLabel")}
                          </Label>
                          <Input
                            id="smbBasePath"
                            value={smbBasePath}
                            onChange={(e) => setSmbBasePath(e.target.value)}
                            placeholder={t("new.smbBasePathPlaceholder")}
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <Button
                          onClick={() => void handleDirectCreate()}
                          disabled={
                            isSubmitting ||
                            !canIssueInitialHandoff ||
                            !displayName.trim() ||
                            smbHost.trim().length === 0 ||
                            smbShareName.trim().length === 0
                          }
                        >
                          {isSubmitting ? t("new.submitting") : t("new.smbRegisterCta")}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <section className="space-y-5 rounded-2xl border border-sky-200 bg-sky-50/70 p-5">
                      <div className="space-y-1">
                        <h3 className="text-lg font-semibold tracking-tight text-slate-950">
                          {t("new.smbRegisteredTitle")}
                        </h3>
                        <p className="text-sm leading-6 text-slate-700">
                          {t("new.smbRegisteredLead")}
                        </p>
                      </div>

                      {setupHandoff ? (
                        <div className="space-y-4">
                          <ol className="space-y-2 text-sm text-slate-700">
                            {(t("detail.smbHandoffSteps", { returnObjects: true }) as unknown as string[]).map((step) => (
                              <li key={step} className="flex gap-2">
                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-600" />
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>
                          <GatewayRegistrationCode
                            registrationToken={setupHandoff.registrationToken}
                            expiresAt={setupHandoff.expiresAt}
                            copied={copiedSetupField === "registrationToken"}
                            onCopy={(event) => void handleCopyRegistrationToken(event)}
                          />
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-3">
                          <Button
                            size="sm"
                            onClick={() =>
                              void (async () => {
                                try {
                                  const handoff =
                                    await issueLocalGatewayRouteHandoff({
                                      storageId: smbCreatedStorageId,
                                    });
                                  setSetupHandoff(handoff);
                                  setCopiedSetupField(null);
                                } catch {
                                  // Error UI is driven by routeHandoffError.
                                }
                              })()
                            }
                            disabled={isIssuingAnyHandoff}
                          >
                            {isIssuingAnyHandoff
                              ? t("detail.localGatewaySetupIssuing")
                              : t("detail.localGatewaySetupIssueCta")}
                          </Button>
                        </div>
                      )}

                      <p className="text-sm leading-6 text-slate-700">{t("new.smbNextStep")}</p>
                      <div className="flex flex-wrap gap-3">
                        <Button
                          onClick={() => navigate(`/console/storages/${smbCreatedStorageId}`)}
                        >
                          {t("new.smbRegisteredOpenDetail")}
                        </Button>
                      </div>
                    </section>
                  )}
                </div>
              )}

              {selectedProcedure === "register_existing" &&
                selectedKind.connectionKindId !== "smb" && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="resourceLocatorInput">
                        {t("new.resourceLocatorInputLabel")}
                      </Label>
                      <Input
                        id="resourceLocatorInput"
                        value={resourceLocatorInput}
                        onChange={(e) => setResourceLocatorInput(e.target.value)}
                        placeholder={t("new.resourceLocatorInputPlaceholder")}
                      />
                      <p className="text-sm text-muted-foreground">
                        {t("new.resourceLocatorInputHelp")}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={() => void handleDirectCreate()}
                        disabled={
                          isSubmitting || !displayName.trim() || resourceLocatorInput.trim().length === 0
                        }
                      >
                        {isSubmitting
                          ? t("new.submitting")
                          : t("new.registerExistingCta")}
                      </Button>
                    </div>
                  </div>
                )}

              {selectedProcedure === "new_create" && (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                    {t("new.newCreateHelp")}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={() => void handleDirectCreate()} disabled={isSubmitting || !displayName.trim()}>
                      {isSubmitting
                        ? t("new.submitting")
                        : t("new.newCreateCta")}
                    </Button>
                  </div>
                </div>
              )}

              {inlineActionError && (
                <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                  {inlineActionError}
                </div>
              )}

              {!selectedProcedure && !selectedKindHasMultipleProcedures && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                  {t("new.awaitingProcedure")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
